import React from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '/images/facilities/Science-Lab.jpg') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const STAGES = [
  {
    step: '01',
    title: 'Transitional Foundation & Inquiry',
    classes: 'Grade VI',
    age: 'Ages 11 to 12 Years',
    icon: 'fa-compass',
    desc: 'Smooth transition from primary self-contained classrooms into departmentalized subjects, specialized laboratories, and structured study habits.',
    keyPoints: [
      'Departmentalized subject transition & timetable mastery',
      'Foundational lab protocols in Physics, Chemistry & Biology',
      'Algebraic concepts, geometry & integer arithmetic',
      'Bilingual fluency, creative writing & public debate introduction'
    ]
  },
  {
    step: '02',
    title: 'Analytical & Experimental Rigour',
    classes: 'Grade VII',
    age: 'Ages 12 to 13 Years',
    icon: 'fa-flask-vial',
    desc: 'Deepening scientific investigation, computational problem-solving, historical inquiry, and inter-disciplinary team presentations.',
    keyPoints: [
      'Hypothesis-driven scientific investigations & lab reports',
      'Introductory Python coding, algorithms & digital safety',
      'Socio-environmental case studies & field observations',
      'Inter-house sporting leagues, athletics & studio arts'
    ]
  },
  {
    step: '03',
    title: 'Pre-Secondary Competence & Leadership',
    classes: 'Grade VIII',
    age: 'Ages 13 to 14 Years',
    icon: 'fa-atom',
    desc: 'Consolidating core academic competencies, abstract reasoning, and leadership responsibilities to ensure seamless readiness for CBSE Board years.',
    keyPoints: [
      'Advanced problem-solving across Mathematics & Sciences',
      'Research-based social science essays & mock parliaments',
      'Computer science, artificial intelligence exposure & design thinking',
      'Student council representation, clubs & community service'
    ]
  }
];

const MiddleSchoolProgrammePage = () => {
  const pageData = usePage('middle-school-programme');
  const sections = pageData.sections || {};
  const banner = sections.banner || pageData.banner || {};

  const title = banner.title || pageData.title || 'Middle School Programme';
  const subtitle = banner.subtitle || 'Conceptual Depth, Experimental STEM & Independent Thinking (Grades VI to VIII)';
  const breadcrumb = banner.breadcrumb || 'MIDDLE SCHOOL';
  const bannerImgSrc = resolveImage(pageData.bannerImage || pageData.banner_image, '/images/facilities/Science-Lab.jpg');

  const overview = sections.overview || {};
  const overviewKicker = overview.kicker || 'TRANSITIONAL & EXPLORATORY YEARS • GRADES VI TO VIII';
  const overviewHeading = overview.heading || 'Nurturing Inquiring Minds, Scientific Temper & Independent Reasoning';
  const overviewParas = overview.paragraphs
    ? (Array.isArray(overview.paragraphs) ? overview.paragraphs : overview.paragraphs.split('\n\n').filter(Boolean))
    : [
        'The middle school years represent an essential transformational bridge—the pivotal phase where children evolve from foundational curiosity into independent, analytical thinkers. At St. Andrews World School, Sonipat, our Middle School Programme is engineered to cultivate critical questioning, experimental investigation, and self-directed discipline.',
        'Aligned with the CBSE Middle School Curriculum and NEP 2020 Pedagogical Framework, our academic approach moves beyond textbook rote learning. Students engage with interdisciplinary STEM modules, experiential mathematics, hands-on science laboratories, and socio-cultural inquiry projects that connect theoretical principles to tangible real-world applications.',
        'We recognize that early adolescence requires thoughtful emotional and intellectual scaffolding. Our dedicated educators mentor students to cultivate constructive debate, responsible digital citizenship, sportsmanship, and empathetic leadership within a vibrant, inclusive campus culture.'
      ];
  const quoteText = overview.quote_text || '“Middle school is where students transition from learning to know, to learning to question and innovate. Our learning ecosystem balances academic rigour with empathetic mentorship to unlock every child’s potential.”';
  const quoteAuthor = overview.quote_author || 'Middle Academic Directorate • SAWS Sonipat';
  const photoBadge = overview.photo_badge || 'Hands-On Discovery';
  const photoTitle = overview.photo_title || 'Advanced STEM & Inquiry-Driven Laboratories';
  const showcaseImg = resolveImage(overview.image, bannerImgSrc);

  const stagesKicker = sections.stages_kicker || 'LEARNING ROADMAP';
  const stagesHeading = sections.stages_heading || 'Age-Calibrated Learning Across Middle School Stages';
  const stagesSubtitle = sections.stages_subtitle || 'Our middle school curriculum progresses systematically to build conceptual mastery, scientific curiosity, and collaborative competence across each grade.';
  const stagesList = (sections.stages && sections.stages.length ? sections.stages : STAGES).map(st => ({
    ...st,
    keyPoints: Array.isArray(st.keyPoints)
      ? st.keyPoints
      : (typeof st.keyPoints === 'string' ? st.keyPoints.split('\n').map(p => p.trim()).filter(Boolean) : (st.keyPoints || []))
  }));

  return (
    <>
      <SEO slug="middle-school-programme" title="Middle School Programme | St. Andrews World School, Sonipat"
        description="Explore the Middle School Programme (Grades VI to VIII) at St. Andrews World School, Sonipat. Fostering inquiry-based science, experiential mathematics, digital literacy, and leadership."
        keywords="Middle School Programme, Middle School Sonipat, Grades 6 to 8 CBSE, Best Middle School Sonipat, SAWS Sonipat"
      />

      {/* 1. HERO BANNER */}
      <PageBanner
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        bgImage={bannerImgSrc}
      />

      {/* 2. OPEN OVERVIEW & PHILOSOPHY (No Box) */}
      <section className="bg-white py-16 md:py-24 border-b border-gray-100">
        <div className="container max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Narrative */}
            <div className="lg:col-span-7">
              <Reveal>
                <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-2 block">
                  {overviewKicker}
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary leading-tight mb-5">
                  {overviewHeading}
                </h2>
                <div className="w-16 h-1 bg-secondary rounded-full mb-6" />

                <div className="space-y-4 text-body text-[16px] md:text-[17px] leading-[1.8]">
                  {overviewParas.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}
                </div>

                {/* Quote Callout */}
                {quoteText && (
                  <div className="mt-8 p-5 md:p-6 rounded-2xl bg-[#f9f8f4] border-l-4 border-secondary">
                    <p className="font-serif italic text-primary text-base md:text-lg font-medium leading-relaxed">
                      {quoteText}
                    </p>
                    {quoteAuthor && (
                      <span className="text-xs font-bold text-secondary uppercase tracking-widest block mt-2">
                        {quoteAuthor}
                      </span>
                    )}
                  </div>
                )}
              </Reveal>
            </div>

            {/* Right Photo Frame (Open, No Card Box) */}
            <div className="lg:col-span-5">
              <Reveal delayMs={120}>
                <div className="relative group">
                  <div className="absolute -inset-3.5 rounded-3xl bg-secondary/15 -z-10 transform -rotate-1 group-hover:rotate-0 transition-transform duration-500" />
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 shadow-xl">
                    <img
                      src={showcaseImg}
                      alt="Middle School Science Lab at St Andrews"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent pointer-events-none" />
                    
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      {photoBadge && (
                        <span className="inline-block px-3 py-1 rounded-full bg-secondary text-primary font-bold text-xs uppercase tracking-wider mb-1">
                          {photoBadge}
                        </span>
                      )}
                      {photoTitle && (
                        <h4 className="text-lg md:text-xl font-display font-bold text-white">
                          {photoTitle}
                        </h4>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      {/* 3. DEVELOPMENTAL STAGES (Open Alternating Flow, No Box) */}
      <section className="bg-[#f9f8f4] py-16 md:py-24">
        <div className="container max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-2 block">
                {stagesKicker}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary mb-4">
                {stagesHeading}
              </h2>
              <div className="w-16 h-1 bg-secondary mx-auto mb-4 rounded-full" />
              {stagesSubtitle && (
                <p className="text-body text-[16px] md:text-[17px] leading-relaxed">
                  {stagesSubtitle}
                </p>
              )}
            </div>
          </Reveal>

          <div className="space-y-16 lg:space-y-20">
            {stagesList.map((st, idx) => {
              const isReversed = idx % 2 === 1;
              return (
                <div key={idx} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  
                  {/* Visual Stage Indicator */}
                  <div className={`lg:col-span-5 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <Reveal delayMs={isReversed ? 100 : 0}>
                      <div className="p-8 md:p-10 rounded-3xl bg-white border border-gray-200/80 shadow-sm relative overflow-hidden">
                        <div className="text-secondary font-display font-bold text-6xl md:text-7xl opacity-30 absolute top-4 right-6">
                          {st.step}
                        </div>
                        <div className="w-14 h-14 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center text-2xl mb-6 shadow-xs">
                          <i className={`fas ${st.icon || 'fa-flask-vial'}`} />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider text-secondary block mb-1">
                          Stage {st.step}
                        </span>
                        <h3 className="text-2xl md:text-3xl font-display font-bold text-primary mb-2">
                          {st.title}
                        </h3>
                        {st.classes && (
                          <div className="inline-block px-3 py-1 rounded-full bg-primary/5 text-primary text-xs font-semibold mb-3 border border-primary/10">
                            {st.classes}
                          </div>
                        )}
                        {st.age && (
                          <div className="text-xs text-muted font-medium flex items-center gap-1.5">
                            <i className="far fa-clock text-secondary" />
                            <span>{st.age}</span>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  </div>

                  {/* Narrative & Key Points */}
                  <div className={`lg:col-span-7 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <Reveal delayMs={isReversed ? 0 : 100}>
                      <div className="space-y-4">
                        <p className="text-body text-[16px] md:text-[17px] leading-[1.8]">
                          {st.desc}
                        </p>
                        
                        {st.keyPoints && st.keyPoints.length > 0 && (
                          <div className="pt-2">
                            <h4 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                              <i className="fas fa-bullseye text-secondary text-xs" />
                              <span>Core Developmental Milestones:</span>
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {st.keyPoints.map((pt, pIdx) => (
                                <div
                                  key={pIdx}
                                  className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-gray-200/70 text-[14px] md:text-[15px] text-body"
                                >
                                  <i className="fas fa-circle-check text-secondary text-sm mt-0.5 shrink-0" />
                                  <span>{pt}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default MiddleSchoolProgrammePage;
